import { motion } from "motion/react";
import { useLang } from "../../i18n/LanguageContext";

const NAV_GROUPS = [
  { label: { en: "WORKSPACE", ar: "مساحة العمل" }, items: [
    { en: "Overview", ar: "نظرة عامة", active: true },
    { en: "Campaigns", ar: "الحملات" },
    { en: "Commerce", ar: "التجارة" },
  ] },
  { label: { en: "AUTOMATION", ar: "الأتمتة" }, items: [
    { en: "AI Agents", ar: "وكلاء الذكاء" },
    { en: "Workflows", ar: "مسارات العمل" },
    { en: "Settings", ar: "الإعدادات" },
  ] },
];

const STAT_TILES = [
  { label: { en: "Active projects", ar: "مشاريع نشطة" }, value: "37", tag: { en: "live", ar: "مباشر" } },
  { label: { en: "AI agents deployed", ar: "وكلاء منشورون" }, value: "184", tag: { en: "▲ 12%", ar: "▲ ١٢٪" } },
  { label: { en: "Uptime (30d)", ar: "التوفر" }, value: "99.9%", tag: { en: "SLA", ar: "اتفاقية" } },
];

/** Dashboard-style mockup card under the hero copy — the "product proof"
 * visual: fake sidebar, stat tiles, a sparkline. Pure decoration (aria-hidden),
 * mirrors for RTL by flipping justify/align, not by mirroring numerals. */
export const HeroMockup = () => {
  const { lang } = useLang();
  const isRtl = lang === "ar";

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      aria-hidden="true"
      dir={isRtl ? "rtl" : "ltr"}
      className="hero-mockup-glow relative mx-auto mt-14 w-full max-w-5xl rounded-2xl border border-soft bg-app-elev/80 backdrop-blur-xl overflow-hidden"
    >
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-hair bg-surface-subtle">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
        <span className="ms-3 text-[11px] mono-label text-soft">gulftechlab.com / overview</span>
      </div>

      <div className="flex flex-col sm:flex-row">
        <nav className="sm:w-44 shrink-0 border-e-0 sm:border-e border-b sm:border-b-0 border-hair px-4 py-4 space-y-5">
          {NAV_GROUPS.map((group) => (
            <div key={group.label.en}>
              <div className="text-[10px] mono-label text-faint mb-2">{group.label[lang]}</div>
              <div className="space-y-1">
                {group.items.map((item) => (
                  <div
                    key={item.en}
                    className={`flex items-center gap-2 text-xs rounded-md px-2 py-1.5 ${
                      item.active ? "bg-cyan-400/10 text-strong" : "text-mid"
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${item.active ? "bg-cyan-400" : "bg-current opacity-40"}`} />
                    {item[lang]}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="flex-1 p-5">
          <div className="grid grid-cols-3 gap-3">
            {STAT_TILES.map((tile) => (
              <div key={tile.label.en} className="rounded-lg border border-hair bg-surface-subtle px-3 py-3">
                <div className="text-[10px] mono-label text-soft mb-1.5">{tile.label[lang]}</div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-xl font-bold text-strong tabular-nums">{tile.value}</span>
                  <span className="text-[10px] text-cyan-300">{tile.tag[lang]}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 rounded-lg border border-hair bg-surface-subtle p-3 h-32">
            <svg viewBox="0 0 400 100" className="w-full h-full" preserveAspectRatio="none">
              <path
                d="M0,70 C40,40 80,80 120,55 C160,30 200,60 240,45 C280,30 320,50 360,20 L400,10"
                fill="none"
                stroke="rgba(0,229,255,0.7)"
                strokeWidth="2"
              />
              <path
                d="M0,70 C40,40 80,80 120,55 C160,30 200,60 240,45 C280,30 320,50 360,20 L400,10 L400,100 L0,100 Z"
                fill="rgba(0,229,255,0.08)"
                stroke="none"
              />
            </svg>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default HeroMockup;
